import { Component, OnInit } from '@angular/core';
import { finalize } from 'rxjs';
import { AdminService } from 'src/app/services/admin/admin.service';
import Quill from 'quill';

const SizeStyle: any = Quill.import('attributors/style/size');

SizeStyle.whitelist = [
  '10px',
  '12px',
  '14px',
  '16px',
  '18px',
  '20px',
  '22px',
  '24px',
  '28px',
  '32px'
];

Quill.register(SizeStyle, true);
export interface VOCCourseDetailsUnitModel {
  id: number;
  fkVocCourseId: number;
  name: string;
  description: string;
  icon: string;
}

export interface VOCCoursePublicModel {
  id: number;
  courseCode: string;
  courseName: string;
  shortDescription: string;
  fullDescription: string;
  duration: string;
  suitableFor: string;
  outComes: string;
  locations: string;
  currencyCode: string;
  price: number;
  isActive: boolean;
  imageUrl: string;
  units: VOCCourseDetailsUnitModel[];
}

export interface PagedResponseModel<T> {
  records: T[];
  totalRecords: number;
}

@Component({
  selector: 'app-admin-voc-course-detail',
  templateUrl: './admin-voc-course-detail.component.html',
  styleUrl: './admin-voc-course-detail.component.scss'
})
export class AdminVocCourseDetailComponent implements OnInit {

  // =========================================================
  // VOC List
  // =========================================================

  allVOCCourses: any[] = [];

  isLoading: boolean = false;

  isRefreshing: boolean = false;


  // =========================================================
  // Pagination
  // =========================================================

  currentPage: number = 1;

  pageSize: number = 10;

  readonly pageSizeOptions: number[] = [10, 20, 50, 100];


  // =========================================================
  // Modal
  // =========================================================

  showVOCModal: boolean = false;

  isEditMode: boolean = false;

  isLoadingDetails: boolean = false;

  isSaving: boolean = false;

  formError: string = '';


  // =========================================================
  // VOC Form
  // =========================================================

  vocForm: any = this.getEmptyVOCModel();
  // ============================================================
  // RICH TEXT EDITOR CONFIG
  // ============================================================


  editorModules = {
    toolbar: [
      [
        {
          size: [
            '10px',
            '12px',
            '14px',
            '16px',
            '18px',
            '20px',
            '22px',
            '24px',
            '28px',
            '32px'
          ]
        }
      ],

      ['bold', 'italic', 'underline', 'strike'],

      [
        { header: 1 },
        { header: 2 }
      ],

      [
        { list: 'ordered' },
        { list: 'bullet' }
      ],

      [
        { indent: '-1' },
        { indent: '+1' }
      ],

      [
        { align: [] }
      ],

      [
        { color: [] },
        { background: [] }
      ],

      ['link'],

      ['clean']
    ]
  };

  constructor(
    private adminService: AdminService
  ) { }


  ngOnInit(): void {

    this.loadVOCList();

  }


  // =========================================================
  // Load VOC List
  // =========================================================

  loadVOCList(): void {

    this.isLoading = true;

    this.adminService
      .getXVOCList('GET_ALL_ADMIN')
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({

         next: (response: any) => {

  const result =
    response?.data ??
    response?.Data ??
    response;

  this.allVOCCourses = Array.isArray(result)
    ? result
    : [];

  // Reset pagination
  this.currentPage = 1;
  this.pageSize = 10;

  this.validateCurrentPage();

  console.log('Total VOC Courses:', this.allVOCCourses.length);
  console.log('Paged VOC Courses:', this.pagedVOCCourses);

},
        error: (error: any) => {

          console.error(
            'Error loading VOC courses:',
            error
          );

          this.allVOCCourses = [];

        }

      });

  }



  // =========================================================
  // Add VOC
  // =========================================================

  openAddVOC(): void {

    this.isEditMode = false;

    this.formError = '';

    this.vocForm =
      this.getEmptyVOCModel();

    this.showVOCModal = true;

  }


  // =========================================================
  // Edit VOC
  // =========================================================

  editVOC(voc: any): void {

    if (!voc?.id) {
      return;
    }

    this.isEditMode = true;

    this.formError = '';

    this.showVOCModal = true;

    this.isLoadingDetails = true;

    this.adminService
      .getXVOCFullDetails(voc.id)
      .pipe(
        finalize(() => {
          this.isLoadingDetails = false;
        })
      )
      .subscribe({

        next: (response: any) => {

          const result =
            response?.data ??
            response?.Data ??
            response?.vocDetails ??
            response?.VocDetails ??
            response;

          const details =
            Array.isArray(result)
              ? result[0]
              : result;

          if (!details) {

            this.formError =
              'Unable to load VOC details.';

            return;
          }

          this.vocForm = {

            id:
              details.id ?? 0,

            fkParentCourseId:
              details.fkParentCourseId ?? null,

            vocCode:
              details.vocCode ?? '',

            vocName:
              details.vocName ?? '',

            description:
              details.description ?? '',

            isActive:
              details.isActive ?? true,

            documentTypes:
              details.documentTypes ?? '',

            delivery:
              details.delivery ?? '',

            location:
              details.location ?? '',

            requisitesRequirements:
              details.requisitesRequirements ?? '',

            certification:
              details.certification ?? '',

            cost:
              details.cost ?? null,

            nShortDescription: details.nShortDescription ?? '',

          };

        },

        error: (error: any) => {

          console.error(
            'Error loading VOC details:',
            error
          );

          this.formError =
            'Unable to load VOC details.';

        }

      });

  }


  // =========================================================
  // Save VOC
  // =========================================================

  saveVOC(): void {

    this.formError = '';

    // Validate VOC code
    if (
      !this.vocForm.vocCode ||
      !this.vocForm.vocCode.trim()
    ) {
      this.formError = 'VOC code is required.';
      return;
    }

    // Validate VOC name
    if (
      !this.vocForm.vocName ||
      !this.vocForm.vocName.trim()
    ) {
      this.formError = 'VOC name is required.';
      return;
    }

    const type =
      this.isEditMode
        ? 'UPDATE'
        : 'INSERT';


    const model = {
      id: this.vocForm.id ?? 0,
      fkParentCourseId:
        this.vocForm.fkParentCourseId || null,

      vocCode:
        this.vocForm.vocCode?.trim() ?? '',

      vocName:
        this.vocForm.vocName?.trim() ?? '',

      description:
        this.vocForm.description ?? '',

      isActive:
        this.vocForm.isActive ?? true,

      documentTypes:
        this.vocForm.documentTypes ?? '',

      delivery:
        this.vocForm.delivery || null,

      location:
        this.vocForm.location || null,

      requisitesRequirements:
        this.vocForm.requisitesRequirements || null,

      certification:
        this.vocForm.certification || null,

      cost:
        this.vocForm.cost === '' ||
          this.vocForm.cost === undefined
          ? null
          : this.vocForm.cost,

      nShortDescription: this.vocForm.nShortDescription || null,
    };


    this.isSaving = true;


    this.adminService
      .SetXVOC(model, type)
      .subscribe({

        next: (response: any) => {

          this.isSaving = false;

          if (
            response?.status === false ||
            response?.Status === false
          ) {
            this.formError =
              response?.message ??
              response?.Message ??
              'Unable to save VOC.';

            return;
          }

          // Close modal after success
          this.closeVOCModal();

          // Reload table
          this.loadVOCList();
        },

        error: (error: any) => {

          this.isSaving = false;

          console.error(
            'Error saving VOC:',
            error
          );

          this.formError =
            error?.error?.message ??
            error?.error?.Message ??
            'Unable to save VOC details.';
        }

      });
  }


  // =========================================================
  // Close Modal
  // =========================================================

  closeVOCModal(): void {

    if (this.isSaving) {
      return;
    }

    this.showVOCModal = false;

    this.isEditMode = false;

    this.isLoadingDetails = false;

    this.formError = '';

    this.vocForm =
      this.getEmptyVOCModel();

  }


  // =========================================================
  // Empty Model
  // =========================================================

  private getEmptyVOCModel(): any {

    return {

      id: 0,

      fkParentCourseId: null,

      vocCode: '',

      vocName: '',

      description: '',

      isActive: true,

      documentTypes: '',

      delivery: '',

      location: '',

      requisitesRequirements: '',

      certification: '',

      cost: null,

      nShortDescription: ''

    };

  }


  // =========================================================
  // Pagination
  // =========================================================

  get filteredVOCCourses(): any[] {
    return this.allVOCCourses;
  }

  get rowsPerPage(): number {
    return this.pageSize;
  }

  set rowsPerPage(value: number) {
    this.pageSize = Number(value) || 10;
    this.onVOCPageSizeChange();
  }

 // Get paginated VOC courses
get pagedVOCCourses(): any[] {

  const startIndex =
    (this.currentPage - 1) * this.pageSize;

  return this.allVOCCourses.slice(
    startIndex,
    startIndex + this.pageSize
  );

}

   // Total pages
 get totalPages(): number {

   return Math.max(
     1,
     Math.ceil(
       this.allVOCCourses.length / this.pageSize
     )
   );

 }

  get totalVOCPages(): number {
    return this.totalPages;
  }

   // Generate page numbers
 get vocPageNumbers(): number[] {

   return Array.from(
     { length: this.totalPages },
     (_, index) => index + 1
   );

 }

   // First visible record
 get startRecord(): number {

   if (this.allVOCCourses.length === 0) {
     return 0;
   }

   return (this.currentPage - 1) * this.pageSize + 1;

 }
// Last visible record
 get endRecord(): number {

   return Math.min(
     this.currentPage * this.pageSize,
     this.allVOCCourses.length
   );

 }


  get vocShowingEnd(): number {
    return this.endRecord;
  }

   // Change page
 changeVOCPage(page: number): void {

   if (
     page < 1 ||
     page > this.totalPages
   ) {
     return;
   }

   this.currentPage = page;

 }

  previousPage(): void {
    this.changeVOCPage(this.currentPage - 1);
  }

  nextPage(): void {
    this.changeVOCPage(this.currentPage + 1);
  }

  onVOCPageSizeChange(): void {
    this.currentPage = 1;
  }

  private validateCurrentPage(): void {
    this.currentPage = Math.max(1, Math.min(this.currentPage, this.totalPages));
  }

  // =========================================================
  // Table Helpers
  // =========================================================

  getShortDescription(
    value: string | null | undefined
  ): string {

    if (!value) {
      return '';
    }

    const div =
      document.createElement('div');

    div.innerHTML = value;

    const text =
      div.textContent ||
      div.innerText ||
      '';

    return text.length > 90
      ? text.substring(0, 90) + '...'
      : text;

  }





  


}